import React, { useState, useCallback, useEffect } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  required,
  TopToolbar,
  ListButton,
  useCreate,
  useRedirect,
  useNotify,
} from 'react-admin';
import { Card, Typography, Box, Divider, Alert } from '@mui/material';
import {
  DataObject as VariableIcon,
  List as ListIcon,
} from '@mui/icons-material';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';

const InputsCreateActions = () => (
  <TopToolbar>
    <ListButton icon={<ListIcon />} />
  </TopToolbar>
);

const InputsCreate = () => {
  const [preselectedTableId, setPreselectedTableId] = useState<string | null>(
    null
  );
  const [filterValues, setFilterValues] =
    useState<HierarchicalFilterSelections>({});

  const handleFilterChange = useCallback(
    (selections: HierarchicalFilterSelections) => {
      setFilterValues(selections);
    },
    []
  );

  useEffect(() => {
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex !== -1) {
      const queryString = hash.substring(queryIndex + 1);
      const urlParams = new URLSearchParams(queryString);
      const tableId = urlParams.get('tableId');
      setPreselectedTableId(tableId);
      if (tableId) {
        setFilterValues({ tableId: parseInt(tableId) });
      }
    }
  }, []);

  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  const handleSave = async (data: any) => {
    try {
      const tableId =
        filterValues.tableId ||
        (preselectedTableId ? parseInt(preselectedTableId) : undefined);
      if (!tableId) {
        notify('Por favor selecciona una tabla antes de crear el input', {
          type: 'error',
        });
        return;
      }
      const payload = {
        variableName: data.variableName,
        varType: 'INPUT',
        description: data.description || null,
        tableId,
      };
      await create('inputs', { data: payload });
      notify('Input creado exitosamente', { type: 'success' });
      redirect('/inputs');
    } catch (error) {
      console.error('Error al crear input:', error);
      notify('Error al crear el input', { type: 'error' });
    }
  };

  return (
    <Create actions={<InputsCreateActions />} redirect={false}>
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <Box sx={{ p: 3 }}>
            <Box display='flex' alignItems='center' mb={3}>
              <VariableIcon
                sx={{ mr: 2, fontSize: 32, color: 'primary.main' }}
              />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Crear Nuevo Input
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Agrega un nuevo input al sistema
                </Typography>
              </Box>
            </Box>

            {/* Filtro Jerárquico para seleccionar planta, zona y tabla */}
            <Card
              sx={{
                p: 3,
                mb: 3,
                boxShadow: 2,
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant='h6' gutterBottom color='primary'>
                Seleccionar Planta, Zona y Tabla
              </Typography>
              <HierarchicalFilter
                levels={['plant', 'zone', 'table']}
                onChange={handleFilterChange}
                initialValues={filterValues}
                size='medium'
                useTableSelector={true}
              />
              {!filterValues.tableId && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  Debes seleccionar una planta, zona y tabla antes de crear el
                  input
                </Alert>
              )}
              {filterValues.tableId && (
                <Alert severity='success' sx={{ mt: 2 }}>
                  ✓ Selección completada: {filterValues.plantName} →{' '}
                  {filterValues.zoneName} → {filterValues.tableName}
                </Alert>
              )}
            </Card>

            <SimpleForm
              onSubmit={handleSave}
              sx={{
                '& .RaSimpleForm-toolbar': {
                  marginTop: 3,
                  padding: 2,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                },
              }}
            >
              <Card
                sx={{
                  p: 3,
                  mb: 3,
                  boxShadow: 2,
                  borderRadius: 2,
                  width: '100%',
                  backgroundColor: 'background.default',
                }}
              >
                <Typography variant='h6' gutterBottom color='primary'>
                  Información del Input
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <TextInput
                  source='variableName'
                  label='Nombre del Input'
                  validate={[required()]}
                  fullWidth
                  sx={{ mb: 3 }}
                  disabled={!filterValues.tableId}
                  helperText='Nombre único para identificar el input'
                />
                <TextInput
                  source='description'
                  label='Descripción (Opcional)'
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={!filterValues.tableId}
                  helperText='Descripción detallada del input'
                />
                <TextInput
                  source='varType'
                  label='Tipo de variable'
                  defaultValue='INPUT'
                  disabled
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Card>
              {!filterValues.tableId && (
                <Alert severity='info'>
                  Completa la selección de planta, zona y tabla arriba para
                  habilitar los campos del formulario
                </Alert>
              )}
            </SimpleForm>
          </Box>
        </Card>
      </Box>
    </Create>
  );
};

export default InputsCreate;
